import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Sector, LabelList
} from 'recharts';
import {
  ArrowRight, Camera, Ruler, Sparkles, ShoppingBag, ChevronRight,
  Globe, Store, Instagram, Package, ShoppingCart,
  ExternalLink, Mail
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO from '../../components/SEO';

// ─── Animation ─────────────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay }
  })
};

// ─── Animated Counter ─────────────────────────────────────────────────────────
function useCountUp(target, duration = 1600, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const p = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(eased * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ─── Metric Card ─────────────────────────────────────────────────────────────
const MetricCard = ({ prefix = '', value, suffix = '', label, sub, delay = 0, color = 'blue' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const count = useCountUp(value, 1400, inView);
  const colorMap = {
    blue: 'text-blue-600', emerald: 'text-emerald-600',
    amber: 'text-amber-700', violet: 'text-violet-600',
  };
  return (
    <motion.div ref={ref} variants={fadeUp} initial="hidden" whileInView="visible"
      custom={delay} viewport={{ once: true }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center hover:shadow-md transition-shadow"
    >
      <div className={`inline-flex items-baseline gap-0.5 text-4xl sm:text-5xl font-bold ${colorMap[color]}`}>
        <span className="text-2xl sm:text-3xl">{prefix}</span>
        <span>{count}</span>
        <span className="text-2xl sm:text-3xl">{suffix}</span>
      </div>
      <p className="mt-2 font-semibold text-slate-800 text-base">{label}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </motion.div>
  );
};

// ─── Return Rate Bar Chart ─────────────────────────────────────────────────────
const ReturnRateChart = () => {
  const data = [
    { category: 'Electronics', rate: 24, color: '#64748b' },
    { category: 'FMCG', rate: 30, color: '#94a3b8' },
    { category: 'Apparel', rate: 40, color: '#2563eb' },
  ];
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} barCategoryGap="35%" margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="category" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
        <Tooltip
          formatter={v => [`${v}%`, 'Return Rate']}
          contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
          labelStyle={{ fontWeight: 600, color: '#0f172a' }}
        />
        <Bar dataKey="rate" radius={[6, 6, 0, 0]}>
          {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ─── Return Reason Donut Chart ─────────────────────────────────────────────────
const ReturnReasonChart = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const data = [
    { name: 'Size mismatch', value: 35, color: '#2563eb' },
    { name: 'Fit uncertainty', value: 25, color: '#3b82f6' },
    { name: 'Expectation gap', value: 20, color: '#60a5fa' },
    { name: 'Style mismatch', value: 10, color: '#93c5fd' },
    { name: 'Other', value: 10, color: '#dbeafe' },
  ];
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
    return (
      <g>
        <text x={cx} y={cy - 10} textAnchor="middle" fill="#0f172a" fontSize={13} fontWeight={700}>{payload.name}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#2563eb" fontSize={22} fontWeight={800}>{value}%</text>
        <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 6} startAngle={startAngle} endAngle={endAngle} fill={fill} />
        <Sector cx={cx} cy={cy} innerRadius={innerRadius - 4} outerRadius={innerRadius - 1} startAngle={startAngle} endAngle={endAngle} fill={fill} />
      </g>
    );
  };
  return (
    <div className="flex flex-col items-center gap-4 sm:gap-6">
      <div className="w-48 h-48 sm:w-64 sm:h-64 flex-shrink-0">
        <PieChart width={192} height={192} className="block sm:hidden">
          <Pie activeIndex={activeIndex} activeShape={(props) => {
            const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
            return (
              <g>
                <text x={cx} y={cy - 8} textAnchor="middle" fill="#0f172a" fontSize={11} fontWeight={700}>{payload.name}</text>
                <text x={cx} y={cy + 10} textAnchor="middle" fill="#2563eb" fontSize={18} fontWeight={800}>{value}%</text>
                <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 4} startAngle={startAngle} endAngle={endAngle} fill={fill} />
                <Sector cx={cx} cy={cy} innerRadius={innerRadius - 3} outerRadius={innerRadius - 1} startAngle={startAngle} endAngle={endAngle} fill={fill} />
              </g>
            );
          }}
            data={data} cx={96} cy={96} innerRadius={52} outerRadius={78}
            dataKey="value" onMouseEnter={(_, i) => setActiveIndex(i)}
          >
            {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
        </PieChart>
        <PieChart width={256} height={256} className="hidden sm:block">
          <Pie activeIndex={activeIndex} activeShape={renderActiveShape}
            data={data} cx={128} cy={128} innerRadius={68} outerRadius={100}
            dataKey="value" onMouseEnter={(_, i) => setActiveIndex(i)}
          >
            {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
        </PieChart>
      </div>
      <div className="flex flex-wrap justify-center sm:justify-start sm:flex-col gap-2 sm:gap-3">
        {data.map((item, i) => (
          <button key={i} onClick={() => setActiveIndex(i)}
            className={`flex items-center gap-2 sm:gap-3 text-left transition-all ${i === activeIndex ? 'opacity-100' : 'opacity-55 hover:opacity-80'}`}
          >
            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
            <span className="text-xs sm:text-sm text-slate-700">
              <span className="font-semibold">{item.value}%</span>: {item.name}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── INR formatter ────────────────────────────────────────────────────────────
const fmt = (n) => {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)     return `₹${n.toLocaleString('en-IN')}`;
  return `₹${n}`;
};

// ─── Slider ───────────────────────────────────────────────────────────────────
const Slider = ({ label, value, min, max, step, onChange, format }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between items-center">
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <span className="text-sm font-semibold text-blue-600">{format(value)}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="w-full h-2 rounded-full appearance-none cursor-pointer"
      style={{ background: `linear-gradient(to right, #2563eb ${((value - min) / (max - min)) * 100}%, #e2e8f0 0%)` }}
    />
  </div>
);

// ─── ROI Comparison Bar Chart ─────────────────────────────────────────────────
const ROIComparisonChart = ({ data }) => {
  // Custom label for Before bars (gray)
  const BeforeLabel = ({ x, y, width, index }) => {
    const item = data[index];
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fill="#64748b"
        fontSize={10}
        fontWeight="600"
      >
        {item.beforeLabel}
      </text>
    );
  };

  // Custom label for After bars (colored)
  const AfterLabel = ({ x, y, width, index }) => {
    const item = data[index];
    const isReturns = item.metric === 'Returns';
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fill={isReturns ? '#059669' : '#2563eb'}
        fontSize={10}
        fontWeight="700"
      >
        {item.afterLabel}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} barCategoryGap="20%" barGap={4} margin={{ top: 25, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="metric" tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false} />
        <YAxis 
          tick={{ fill: '#94a3b8', fontSize: 10 }} 
          axisLine={false} 
          tickLine={false}
          ticks={[0, 50, 100, 150]}
          domain={[0, 150]}
          tickFormatter={(value) => value === 100 ? '100 (Baseline)' : value}
        />
        <Bar dataKey="Before" fill="#cbd5e1" radius={[4, 4, 0, 0]}>
          <LabelList content={<BeforeLabel />} />
        </Bar>
        <Bar dataKey="After" radius={[4, 4, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.metric === 'Returns' ? '#10b981' : '#2563eb'} />
          ))}
          <LabelList content={<AfterLabel />} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// ─── Merged ROI Simulator ─────────────────────────────────────────────────────
const ROISimulator = () => {
  const [orders,     setOrders]    = useState(5000);
  const [aov,        setAov]       = useState(2100);
  const [returnRate, setReturnRate]= useState(30);
  const [logistics,  setLogistics] = useState(150);

  // Nanokart fixed impact benchmarks
  const convUplift = 35;  // 35% more conversions
  const returnRedn = 30;  // 30% reduction in returns

  // Step 1: Current revenue (orders × AOV)
  const currentRevenue = orders * aov;

  // Step 2: Projected revenue with +35% conversion uplift
  const projRevenue = currentRevenue * (1 + convUplift / 100);

  // Step 3: Revenue uplift
  const revenueUplift = projRevenue - currentRevenue;

  // Step 4: Current returns (orders × return rate)
  const currentReturns = orders * (returnRate / 100);

  // Step 5: Return savings = prevented returns × (AOV + logistics cost)
  const preventedReturns = currentReturns * (returnRedn / 100);
  const reducedReturns = currentReturns - preventedReturns;
  const returnSavings = preventedReturns * (aov + logistics);

  // Step 6: Annual impact
  const annualImpact = (revenueUplift + returnSavings) * 12;

  // Format number for display (compact)
  const fmtCompact = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000)     return n.toLocaleString('en-IN');
    return n.toString();
  };

  // Chart data with dynamic labels - Before is always 100, After reflects the change
  const chartData = [
    { 
      metric: 'Revenue', 
      Before: 100, 
      After: 135, 
      beforeLabel: fmtCompact(Math.round(currentRevenue)),
      afterLabel: fmtCompact(Math.round(projRevenue))
    },
    { 
      metric: 'Conversions', 
      Before: 100, 
      After: 135, 
      beforeLabel: orders.toLocaleString('en-IN'),
      afterLabel: Math.round(orders * 1.35).toLocaleString('en-IN')
    },
    { 
      metric: 'Returns', 
      Before: 100, 
      After: 70, 
      beforeLabel: Math.round(currentReturns).toLocaleString('en-IN'),
      afterLabel: Math.round(reducedReturns).toLocaleString('en-IN')
    },
  ];

  const outCards = [
    { label: 'Current Monthly Revenue',    value: fmt(Math.round(currentRevenue)),  bg: 'bg-slate-50 border-slate-100', vClr: 'text-slate-700' },
    { label: 'Projected Monthly Revenue',  value: fmt(Math.round(projRevenue)),     bg: 'bg-blue-50 border-blue-100',   vClr: 'text-blue-700' },
    { label: 'Monthly Revenue Uplift',     value: fmt(Math.round(revenueUplift)),   bg: 'bg-emerald-50 border-emerald-100', vClr: 'text-emerald-700' },
    { label: 'Monthly Return Savings',     value: fmt(Math.round(returnSavings)),   bg: 'bg-emerald-50 border-emerald-100', vClr: 'text-emerald-700' },
    { label: 'Total Annual Business Impact',value: fmt(Math.round(annualImpact)),   bg: 'bg-blue-600 border-blue-600',  vClr: 'text-white', labelClr: 'text-blue-100' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="roi-simulator-widget">
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">

        {/* ── Left: Inputs ── */}
        <div className="p-6 sm:p-8 space-y-5">
          <div>
            <h3 className="font-bold text-slate-900 mb-0.5">Your Business Inputs</h3>
            <p className="text-sm text-slate-500">Adjust sliders to match your brand</p>
          </div>

          <Slider label="Monthly Orders" value={orders} min={500} max={100000} step={500}
            onChange={setOrders} format={v => v.toLocaleString('en-IN')} />
          <Slider label="Average Order Value (AOV)" value={aov} min={500} max={1000000} step={500}
            onChange={setAov} format={v => `₹${v.toLocaleString('en-IN')}`} />
          <Slider label="Current Return Rate" value={returnRate} min={5} max={60} step={1}
            onChange={setReturnRate} format={v => `${v}%`} />
          <Slider label="Reverse Logistics Cost per Return" value={logistics} min={50} max={500} step={10}
            onChange={setLogistics} format={v => `₹${v}`} />

          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs text-slate-500 mb-3">
              <span className="font-semibold">Nanokart benchmarks applied:</span> 20-40% conversion uplift and 25-35% return reduction (we use mid-range: 35% and 30%).
            </p>
            
            {/* Formula Reference */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">How Nanokart ROI is Calculated</p>
              <div className="space-y-1.5 text-[11px] text-slate-600">
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono">1.</span>
                  <span><span className="font-medium">Current Revenue</span> = Orders × AOV</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono">2.</span>
                  <span><span className="font-medium">Projected Revenue</span> = Current × (1 + 35% conversion uplift)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono">3.</span>
                  <span><span className="font-medium">Revenue Uplift</span> = Projected − Current</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono">4.</span>
                  <span><span className="font-medium">Returns Prevented</span> = Orders × Return% × 30% reduction</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono">5.</span>
                  <span><span className="font-medium">Return Savings</span> = Prevented × (AOV + Logistics)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-slate-400 font-mono">6.</span>
                  <span><span className="font-medium">Annual Impact</span> = (Uplift + Savings) × 12</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Outputs ── */}
        <div className="p-6 sm:p-8 flex flex-col gap-5">
          <div>
            <h3 className="font-bold text-slate-900 mb-0.5">Example Impact for Your Inputs</h3>
            <p className="text-sm text-slate-500">Based on industry benchmarks for AI try-on</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {outCards.slice(0, 4).map((c, i) => (
              <div key={i} className={`rounded-xl p-3 border ${c.bg} transition-all duration-200`}>
                <div className={`text-xs font-medium mb-1 ${c.labelClr || 'text-slate-500'}`}>{c.label}</div>
                <div key={c.value} className={`text-xl font-bold ${c.vClr} transition-all duration-200`}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Annual Impact - full width */}
          <div className={`rounded-xl p-4 border ${outCards[4].bg} flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 transition-all duration-200`}>
            <div>
              <div className="text-xs font-medium text-blue-200 mb-0.5">Estimated Annual Revenue Impact</div>
              <div key={outCards[4].value} className="text-2xl sm:text-3xl font-bold text-white transition-all duration-200">{outCards[4].value}</div>
            </div>
            <div className="text-blue-200 text-xs sm:text-right leading-relaxed">
              Revenue uplift +<br className="hidden sm:block" /> return savings<br className="hidden sm:block" /> × 12 months
            </div>
          </div>

          {/* Micro-CTA */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-sm text-slate-700">
              <span className="font-medium">Want a custom ROI report for your brand?</span>
            </p>
            <Link to="/contact" className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-all">
              Book a Demo <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Comparison chart */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-slate-600">Before vs After Nanokart</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className="w-3 h-2 rounded-sm bg-slate-200 inline-block" /> Before
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                  <span className="w-3 h-2 rounded-sm bg-blue-500 inline-block" /> After
                </span>
              </div>
            </div>
            <ROIComparisonChart data={chartData} />
            <p className="text-[10px] text-slate-400 mt-2 text-center">
              Revenue & Conversions: higher is better · Returns: lower is better
            </p>
          </div>

          <p className="text-xs text-slate-400 leading-relaxed">
            Estimates are based on industry benchmarks for AI-assisted shopping experiences.
            Actual results may vary by brand and product category.
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN PAGE - STREAMLINED 5-SECTION VERSION ─────────────────────────────────
const WhyNanokart = () => {
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    // Wait for page to fully render, then scroll to the anchor
    const id = hash.replace('#', '');
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    // Small delay to let React finish rendering
    const t = setTimeout(scroll, 300);
    return () => clearTimeout(t);
  }, []);

  return (
  <MarketingLayout>
    <SEO
      title="Why Nanokart | Conversion & Return Optimization for Fashion Brands"
      description="See how Nanokart helps fashion brands boost conversions and slash returns with AI-powered try-on and smart size recommendations. Real outcomes, measurable ROI."
      keywords={['conversion optimization fashion', 'reduce fashion returns', 'fashion brand ROI', 'size recommendation AI', 'return rate reduction ecommerce', 'fashion commerce optimization']}
      canonicalUrl="https://nanokart.ai/why-nanokart"
    />

    {/* ══════════════════════════════════════════════════════════════════════════
        SECTION 1: HERO WITH LIVE DEMO + PROBLEM FRAMING
        Goal: Show the magic in 5 seconds, then frame the problem
    ══════════════════════════════════════════════════════════════════════════ */}
    <section className="pt-24 sm:pt-28 pb-16 sm:pb-20 bg-gradient-to-b from-slate-50 to-white" data-testid="why-hero-section">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Message */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold mb-5">
              <Sparkles className="w-3.5 h-3.5" />
              TryOn Studio™
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              Fashion brands lose <span className="text-red-500">25%<sup className="text-red-400 text-lg ml-0.5">¹</sup></span> of revenue to returns.
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-blue-600 mb-4">
              Nanokart cuts that in half.
            </p>
            <p className="text-base text-slate-500 leading-relaxed mb-6">
              AI-powered try-on lets customers see themselves in your products before buying, 
              reducing returns and increasing conversions.
            </p>
            {/* Quick Stats Row */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-600 font-bold text-sm">↓</span>
                </div>
                <span className="text-sm font-semibold text-slate-700">30% Fewer Returns<sup className="text-slate-400 text-[10px] ml-0.5">²</sup></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">↑</span>
                </div>
                <span className="text-sm font-semibold text-slate-700">35% More Conversions<sup className="text-slate-400 text-[10px] ml-0.5">²</sup></span>
              </div>
            </div>
            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20">
                Book a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/tryon-studio" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 rounded-full font-semibold hover:bg-slate-200 transition-all">
                See How It Works
              </Link>
            </div>
          </motion.div>

          {/* Right: Visual Demo */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.2}
            className="relative"
          >
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8">
              <p className="text-center text-sm font-semibold text-slate-600 mb-4">See the AI magic in action</p>
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                {/* Customer Photo */}
                <div className="text-center">
                  <div className="w-24 sm:w-32 aspect-[3/4] rounded-xl overflow-hidden shadow-lg border-2 border-white mb-2">
                    <img 
                      src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110203941.webp" 
                      alt="Customer" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs text-slate-500">Customer Photo</span>
                </div>
                {/* Arrow */}
                <div className="flex flex-col items-center gap-1">
                  <Sparkles className="w-5 h-5 text-blue-500" />
                  <ArrowRight className="w-6 h-6 text-blue-400" />
                  <span className="text-[10px] font-bold text-blue-500">AI</span>
                </div>
                {/* Try-On Result */}
                <div className="text-center">
                  <div className="w-24 sm:w-32 aspect-[3/4] rounded-xl overflow-hidden shadow-xl ring-2 ring-blue-400 mb-2">
                    <img 
                      src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110204027.webp" 
                      alt="Try-On Result" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-xs font-semibold text-blue-600">Try-On Result</span>
                </div>
              </div>
              <p className="text-center text-xs text-slate-400 mt-4">Photorealistic results in under 10 seconds</p>
            </div>
          </motion.div>
        </div>

        {/* ── Problem Framing: 80% of Fashion Returns ── */}
        <div className="mt-16 sm:mt-20 pt-12 border-t border-slate-200">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              80% of Fashion Returns Come Down to Just 3 Reasons.<sup className="text-slate-400 text-base ml-0.5">¹</sup>
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Size mismatch, fit uncertainty, and style doubt destroy your profit margins. Nanokart solves all three before the checkout.
            </p>
          </motion.div>

          {/* Problem → Solution Grid */}
          <div className="grid md:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                problem: 'Size Mismatch',
                stat: '35%',
                ref: '¹',
                quote: '"I ordered M but needed L"',
                solution: 'AI Size Recommendation',
                solutionDesc: 'Analyzes body measurements against your size chart',
                icon: Ruler,
                color: 'blue'
              },
              {
                problem: 'Fit Uncertainty',
                stat: '25%',
                ref: '¹',
                quote: '"It didn\'t look like the photos"',
                solution: 'Virtual Try-On',
                solutionDesc: 'Customers see themselves wearing your products',
                icon: Camera,
                color: 'indigo'
              },
              {
                problem: 'Style Doubt',
                stat: '20%',
                ref: '¹',
                quote: '"Not sure when I\'d wear this"',
                solution: 'Lifestyle Moments',
                solutionDesc: 'Shows outfits in real-life contexts',
                icon: Sparkles,
                color: 'violet'
              },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible"
                custom={i * 0.1} viewport={{ once: true }}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Problem Part */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 border-b border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-slate-700">{item.problem}</span>
                    <span className="text-lg font-bold text-red-500">{item.stat}<sup className="text-red-300 text-[10px] ml-0.5">{item.ref}</sup></span>
                  </div>
                  <p className="text-xs text-slate-500 italic">{item.quote}</p>
                </div>
                {/* Solution Part */}
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      item.color === 'blue' ? 'bg-blue-100' : item.color === 'indigo' ? 'bg-indigo-100' : 'bg-violet-100'
                    }`}>
                      <item.icon className={`w-4 h-4 ${
                        item.color === 'blue' ? 'text-blue-600' : item.color === 'indigo' ? 'text-indigo-600' : 'text-violet-600'
                      }`} />
                    </div>
                    <div>
                      <span className={`text-sm font-bold ${
                        item.color === 'blue' ? 'text-blue-600' : item.color === 'indigo' ? 'text-indigo-600' : 'text-violet-600'
                      }`}>{item.solution}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">{item.solutionDesc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>

    {/* ══════════════════════════════════════════════════════════════════════════
        SECTION 2: ROI CALCULATOR
        Goal: Prove the business value — directly linkable via #roi-calculator
    ══════════════════════════════════════════════════════════════════════════ */}
    <section id="roi-calculator" className="py-14 sm:py-16 bg-slate-50 scroll-mt-20" data-testid="impact-section">
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Calculate Your Revenue Impact with Nanokart
          </h2>
          <p className="text-slate-500">
            Input your metrics to see your projected revenue uplift and return savings.
          </p>
        </motion.div>

        {/* ROI Calculator */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.1} viewport={{ once: true }}>
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
              <Sparkles className="w-3 h-3" /> Estimate the Impact for Your Brand
            </span>
          </div>
          <ROISimulator />
        </motion.div>

        {/* Industry Benchmarks Label */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.15} viewport={{ once: true }} className="text-center mt-10 mb-3">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Industry Benchmarks for AI Try-On & Size Recommendation</span>
        </motion.div>

        {/* Compact Metrics Row */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2} viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4"
        >
          {[
            { value: '20-40%', label: 'Higher Conversions', color: 'text-blue-600', bg: 'bg-blue-50', ref: '²' },
            { value: '25-35%', label: 'Fewer Returns', color: 'text-emerald-600', bg: 'bg-emerald-50', ref: '²' },
            { value: '2×', label: 'More Engagement', color: 'text-violet-600', bg: 'bg-violet-50', ref: '²' },
            { value: '10-20%', label: 'Higher AOV', color: 'text-amber-600', bg: 'bg-amber-50', ref: '²' },
          ].map((m, i) => (
            <div key={i} className={`${m.bg} rounded-xl p-4 text-center`}>
              <div className={`text-2xl sm:text-3xl font-bold ${m.color}`}>{m.value}<sup className="text-slate-400 text-xs ml-0.5">{m.ref}</sup></div>
              <div className="text-xs font-semibold text-slate-600 mt-1">{m.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Benchmark Source */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.25} viewport={{ once: true }} className="text-center">
          <p className="text-[10px] text-slate-400">
            Benchmarks based on industry studies and early adoption patterns for AI-assisted shopping experiences.
          </p>
        </motion.div>
      </div>
    </section>

    {/* ══════════════════════════════════════════════════════════════════════════
        SECTION 4: THE THREE CAPABILITIES (Feature Cards)
        Goal: Deep dive into how each feature works
    ══════════════════════════════════════════════════════════════════════════ */}
    <section className="py-14 sm:py-16 bg-white" data-testid="solution-section">
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Three AI Capabilities. One Platform.
          </h2>
          <p className="text-slate-500">
            Everything your customers need to buy with confidence.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
          {/* Card 1: Virtual Try-On */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible"
            viewport={{ once: true }}
            className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
          >
            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-5 h-[180px] sm:h-[200px] flex flex-col">
              <div className="flex justify-end mb-2">
                <span className="px-2.5 py-1 bg-blue-600 text-white text-[10px] font-bold rounded-full">+35% Conversions</span>
              </div>
              <div className="flex-1 flex items-center justify-center gap-2 sm:gap-3">
                <div className="w-14 sm:w-16 aspect-[3/4] rounded-xl overflow-hidden shadow-md border-2 border-white">
                  <img src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110203941.webp" alt="Customer" className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col items-center gap-0.5"><Sparkles className="w-3 h-3 text-blue-500" /><ArrowRight className="w-4 h-4 text-blue-400" /></div>
                <div className="w-14 sm:w-16 aspect-[3/4] rounded-xl overflow-hidden shadow-lg ring-2 ring-blue-400">
                  <img src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110204027.webp" alt="Try-On" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center"><Camera className="w-4 h-4 text-blue-600" /></div>
                <h3 className="font-bold text-slate-900">Virtual Try-On</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">Customers see themselves in your products. No guessing, no hesitation.</p>
            </div>
          </motion.div>

          {/* Card 2: Size Recommendation */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.1}
            viewport={{ once: true }}
            className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
          >
            <div className="relative bg-gradient-to-br from-indigo-50 to-violet-50 p-4 sm:p-5 h-[180px] sm:h-[200px] flex flex-col">
              <div className="flex justify-end mb-2">
                <span className="px-2.5 py-1 bg-emerald-600 text-white text-[10px] font-bold rounded-full">-30% Returns</span>
              </div>
              <div className="flex-1 flex items-center justify-center gap-3">
                <div className="w-14 sm:w-16 aspect-[3/4] rounded-xl overflow-hidden shadow-md border-2 border-white">
                  <img src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260215085750.webp" alt="Size" className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  <div className="flex gap-1">
                    {['S', 'M', 'L'].map((s) => (
                      <div key={s} className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold ${s === 'M' ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-400'}`}>{s}</div>
                    ))}
                  </div>
                  <div className="bg-white rounded-lg px-3 py-1.5 shadow-sm border border-slate-100">
                    <span className="text-[10px] font-bold text-indigo-600">94% Fit Match</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center"><Ruler className="w-4 h-4 text-indigo-600" /></div>
                <h3 className="font-bold text-slate-900">Smart Size</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">AI recommends the perfect size, eliminating the #1 return reason.</p>
            </div>
          </motion.div>

          {/* Card 3: Lifestyle Moments */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" custom={0.2}
            viewport={{ once: true }}
            className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col sm:col-span-2 md:col-span-1"
          >
            <div className="relative bg-gradient-to-br from-violet-50 to-pink-50 p-4 sm:p-5 h-[180px] sm:h-[200px] flex flex-col">
              <div className="flex justify-end mb-2">
                <span className="px-2.5 py-1 bg-violet-600 text-white text-[10px] font-bold rounded-full">2× Engagement</span>
              </div>
              <div className="flex-1 flex items-center">
                <div className="grid grid-cols-4 gap-1.5 w-full">
                  {[
                    { label: 'Work', img: 'https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110204049.webp', active: false },
                    { label: 'Evening', img: 'https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110204133.webp', active: true },
                    { label: 'Casual', img: 'https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110204105.webp', active: false },
                    { label: 'Travel', img: 'https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260110204152.webp', active: false },
                  ].map((m, i) => (
                    <div key={i} className={`relative rounded-lg overflow-hidden aspect-[3/4] ${m.active ? 'ring-2 ring-violet-500 scale-105 z-10' : 'opacity-60'}`}>
                      <img src={m.img} alt={m.label} className="w-full h-full object-cover object-top" />
                      <div className={`absolute bottom-0 left-0 right-0 py-0.5 text-center text-[7px] font-semibold ${m.active ? 'bg-violet-600 text-white' : 'bg-black/50 text-white'}`}>{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center"><Sparkles className="w-4 h-4 text-violet-600" /></div>
                <h3 className="font-bold text-slate-900">Lifestyle Moments</h3>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">Show outfits in real-life contexts: work, dates, weekends, travel.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════════════════════════════════════════
        REFERENCES SECTION
    ══════════════════════════════════════════════════════════════════════════ */}
    <section className="py-8 sm:py-10 bg-slate-100" data-testid="references-section">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <h3 className="text-sm font-semibold text-slate-600 mb-4">Sources & References</h3>
          <div className="space-y-3">
            <div className="flex gap-3 text-xs text-slate-500">
              <span className="font-semibold text-slate-600 flex-shrink-0">¹</span>
              <p>
                Bhatt, S. & Singh, R. (2023). "A Study on Reasons for Product Returns in E-Commerce." 
                <em> International Journal of Management and Humanities (IJMH)</em>, Vol. 10, Issue 2. 
                <a 
                  href="https://www.ijmh.org/wp-content/uploads/papers/v10i2/B16571010223.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline ml-1"
                >
                  Read Paper <ExternalLink className="w-3 h-3 inline ml-0.5" />
                </a>
              </p>
            </div>
            <div className="flex gap-3 text-xs text-slate-500">
              <span className="font-semibold text-slate-600 flex-shrink-0">²</span>
              <p>
                Industry benchmarks based on aggregated data from fashion brands implementing AI-assisted shopping experiences. 
                Results may vary by brand, product category, and implementation. Contact us for case studies specific to your segment.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ══════════════════════════════════════════════════════════════════════════
        SECTION 5: FINAL CTA (with Omnichannel mention)
        Goal: Clear next step
    ══════════════════════════════════════════════════════════════════════════ */}
    <section className="py-14 sm:py-16 bg-blue-600" data-testid="final-cta-section">
      <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
            Ready to Cut Returns in Half?
          </h2>
          <p className="text-blue-100 text-base sm:text-lg mb-6 leading-relaxed">
            Join fashion brands using AI to increase conversions and reduce returns across D2C, Shopify, social commerce, and in-store.
          </p>
          
          {/* Omnichannel Icons */}
          <div className="flex items-center justify-center gap-3 mb-6">
            {[Globe, ShoppingCart, Instagram, Store].map((Icon, i) => (
              <div key={i} className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <Icon className="w-4 h-4 text-blue-100" />
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/contact" data-testid="final-book-demo-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-blue-600 rounded-full font-semibold hover:bg-blue-50 transition-all shadow-lg"
            >
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:team@nanokart.ai" data-testid="final-contact-sales-btn"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-400 transition-all"
            >
              <Mail className="w-4 h-4" /> Contact Sales
            </a>
          </div>
          <p className="text-blue-200 text-sm mt-5">
            team@nanokart.ai · +91-933-77-23626
          </p>
        </motion.div>
      </div>
    </section>
  </MarketingLayout>
  );
};

export default WhyNanokart;
